import { Box, Button, CircularProgress, Modal } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useHistory, useParams } from "react-router-dom";
import api from "services/api";

import { TemplateDataGovernanceModal } from "./TemplateDataGovernance.Modal";
import { IResponse } from "./TemplateDataGovernance.types";
import { useState } from "react";
import { TemplateDataGovernanceTable } from "./TemplateDataGovernance.Table";
import { Close } from "@mui/icons-material";
import Dropzone from "react-dropzone";
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { UploadContainer } from "./TemplateDataGovernance.styled";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useAlert } from "contexts/alert";
import layoutPjsUpload from "assets/documents/layout_pjs_upload.xlsx?url";

export function TemplateDataGovernanceView() {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string; };
  const history = useHistory();
  const { openAlert } = useAlert();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  
  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["data-governance", workspaceId, templateId],
    queryFn: () => api.get<{ data: IResponse }>(`/v2/workspaces/${workspaceId}/templates/${templateId}/data-governance`),
    select: (data) => data.data.data,
    refetchInterval: 5000,
  });

  async function handleUpload() {
    try {
      if (!file) throw new Error("File is mandatory.");

      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file as Blob);
  
      await api.post(`/v2/workspaces/${workspaceId}/templates/${templateId}/pj`, formData, { headers: { "Content-Type": "multipart/form-data" }});

      openAlert('Arquivo está sendo processado e já atualizará a tela', 'success');
    } catch (error) {
      //@ts-ignore
      if (error?.response?.data?.data?.message) {
        //@ts-ignore
        openAlert(error?.response?.data?.data?.message, 'error');
      } else {
        openAlert('Houve algum erro ao enviar o arquivo.', 'error');
      }
    } finally {
      setFile(null);
      setIsUploading(false);
      setIsModalUploadOpen(false);
    }
  }

  function handleDownloadTemplate() {
      const link = document.createElement('a');
      link.href = layoutPjsUpload;
      link.download = 'layout_pjs_upload.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  
  return (
    <>
      {!isFetched && isFetching && <div>Loading...</div>}
      {!isFetched && isError && <div>Error...</div>}
      {data && (
        <>
          <Modal open={isModalUploadOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 900, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
              <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
                <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Carregar PJs</h6>
                <Close style={{"cursor":"pointer"}} 
                  onClick={() => {
                    setFile(null)
                    setIsModalUploadOpen(false)
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
                <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>
                  Faça upload de um arquivo Excel (
                  <span 
                    onClick={handleDownloadTemplate}
                    style={{ 
                      color: '#1976d2', 
                      cursor: 'pointer', 
                      textDecoration: 'underline',
                      fontWeight: '500'
                    }}
                  >
                    use este template
                  </span>
                  ) para adicionar, modificar ou remover pessoas jurídicas da base total de vidas. Para saber a lista de valores de cada coluna, consultar na nossa 
                  <a href='https://app.clickup.com/9007078573/v/dc/8cdu75d-5093/8cdu75d-21593' target="_blank"> documentação.</a>
                </span>
                {!!file && (
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                    <InsertDriveFileOutlinedIcon style={{ width: "24px" }}/>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#191C1A' }}>{file.name}</span>
                    <DeleteIcon color="error" style={{ width: "24px", cursor: "pointer" }} onClick={() => setFile(null)}/>
                  </div>
                )}
                {!file && (
                  <Dropzone
                    onDrop={(acceptedFiles) => !!acceptedFiles.length ? setFile(acceptedFiles[0]) : setFile(null)}
                    accept={{
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                    }}
                  >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <div className="container" style={{ marginTop: "-24px" }}>
                        <UploadContainer {...getRootProps({ className: 'dropzone' })}>
                          <input {...getInputProps()} />
                          <FileUploadIcon style={{ fontSize: '24px', color: '#3024E1' }} />
                          {file ? (
                            <p style={{ color: "#3024E1", fontSize: '16px', margin: '0', fontWeight: 500 }}>
                              Arquivo selecionado
                            </p>
                          ) : isDragActive ? (
                            <>
                              <p style={{ color: "#3024E1", fontSize: '16px', margin: '0' }}>Solte o arquivo aqui…</p>
                              <p style={{ fontSize: "12px", margin: '0', color: '#0F0F1E99' }}>Liberando para upload</p>
                            </>
                          ) : (
                            <>
                              <p style={{ color: "#3024E1", fontSize: '16px', margin: '0' }}>Arraste e solte ou clique para procurar</p>
                              <p style={{ fontSize: "12px", margin: '0', color: '#0F0F1E99' }}>Somente arquivos no formato csv são aceitos</p>
                            </>
                          )}
                        </UploadContainer>
                      </div>
                    )}
                  </Dropzone>
                )}
              </div>
              <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
                  <div style={{"display":"flex","gap":"0.25rem"}}>
                    <Button
                      variant='outlined'
                      color='error'
                      style={{minWidth: "180px","paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                      onClick={() => {
                        setFile(null)
                        setIsModalUploadOpen(false)
                      }}
                      disabled={isUploading}
                    >
                      Cancelar
                    </Button>
        
                    <Button
                      variant='contained'
                      style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                      onClick={handleUpload}
                      disabled={isUploading || !file}
                    >
                      {!isUploading ? "Carregar" : <CircularProgress size="20px"/>}
                    </Button>
                  </div>
                </div>
            </Box>
          </Modal>
          <TemplateDataGovernanceModal activities={data.activities} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600,marginBottom: '24px'}}>
              Governança de dados
            </h1>

            <div style={{"display":"flex","gap":"0.25rem", alignItems: "center"}}>
              <Button variant="outlined" style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "8px", height: "30px", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                onClick={() => setIsModalOpen(true)}
              >
                Atividades relacionadas
              </Button>
            </div>
          </div>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px" }}>
            {data.metrics.map(metric => (
              <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{metric.label}</p>
                <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{metric.value}</span>
              </div>
            ))}
          </div>
          <TemplateDataGovernanceTable data={data.people} />
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
            {data.activities.filter(activity => activity.type === "APP").map(activity => (
              <>
                {activity.slug === "app_upload_layout" ? (
                  <Button
                    key={activity.workspaceActivityId}
                    variant='outlined'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={() => setIsModalUploadOpen(true)}
                  >
                    {activity.name}
                  </Button>
                ) : (
                  <Button
                    key={activity.workspaceActivityId}
                    variant='outlined'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    onClick={() => history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/${activity.slug}/${activity.workspaceActivityId}`)}
                  >
                    {activity.name}
                  </Button>
                )}
              </>
            ))}
          </div>
        </>
      )}
    </>
  );
}
